import { DatePipe, formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, LOCALE_ID, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { CustomerEntity } from '../../../../dave-data-module/entities/customer.entity';
import { FolderTypes } from '../../../../dave-data-module/entities/folder.entity';
import { ContractGroupTypeEntity } from '../../../../dave-data-module/entities/mdqx/contract-group-type.entity';
import { TEGueltigkeitDauerUnits } from '../../../../dave-data-module/entities/mdqx/contract-options.entity';
import { DmsViewStyle } from '../../../../dave-data-module/entities/user.entity';
import { FrontendDate } from '../../../../dave-data-module/helper/backend-frontend-conversion.helper';
import { FolderDataService } from '../../../../dave-data-module/services/folder-data.service';
import { State } from '../../../../dave-data-module/State';
import { PatchContractOptionsRequest } from '../../../../dave-data-module/State/actions/mdqx/contract-options.actions';
import { DeleteContractRequest, PatchContractRequest } from '../../../../dave-data-module/State/actions/mdqx/contract.actions';
import { getCustomers } from '../../../../dave-data-module/State/selectors/customers.selectors';
import { getContractById, getContractGroupType, getContractOptionsByContractId, getContractParticipants, getContractStatus, getTEGueltigkeiten } from '../../../../dave-data-module/State/selectors/mdqx/selectors';
import { getSetting } from '../../../../dave-data-module/State/selectors/users.selectors';
import { CustomPropertyType, IProfileTemplateData } from '../../../../dave-utils-module/dave-shared-components-module/components/detail-views/profile-template/profile-template.component';
import { RoutingHistoryService } from '../../../../dave-utils-module/dave-shared-components-module/services/routing-history.service';
import { isNotNullOrUndefined } from '../../../../helper/helper';
import { CustomerAdministrationMeta } from '../../../../helper/page-metadata';
import { arrayLengthValidator, numberValidator } from '../../../../helper/validation.helper';
import { DeletedDialogComponent } from '../../deleted-dialog/deleted-dialog.component';
import { SelectCustomersForContractDialogComponent, SelectCustomersForContractDialogData } from '../select-customers-for-contract-dialog/select-customers-for-contract-dialog.component';
import {
    PermissionService
} from '../../../../dave-utils-module/dave-shared-components-module/services/permission.service';

type CustomerDisplayObject = {
    Id: number,
    CustomerTypeIds: number[],
    CustomerNo: string,
    Name: string,
    Duration: string,
    AusnahmeDatum: string,
}
@Component({
    selector: 'app-detail-contract',
    templateUrl: './detail-contract.component.html',
    styleUrls: ['./detail-contract.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DetailContractComponent implements OnDestroy {
    private contractGroupTypeList$ = this.store.select(getContractGroupType).pipe(filter(isNotNullOrUndefined));
    private subscriptions: Subscription[] = [];
    public Save$ = new Subject<void>();
    public OpenEdit$ = new Subject<void>();
    public OpenDeletedDialog$ = new Subject<void>();
    public OpenCustomerSelect$ = new Subject<void>();
    public Editing$ = new BehaviorSubject<boolean>(false);
    public ProfileData$: Observable<IProfileTemplateData>;
    public CustomerSearchForm = new FormControl<string>('');
    public SearchedCustomerList$: Observable<CustomerDisplayObject[]>;

    public ViewStyle$ = this.store.select(getSetting).pipe(map((setting) => setting?.DmsViewStyle));
    public FileViewStyles = DmsViewStyle;
    public FolderId$: Observable<number>;
    // Für den Filter
    public FilterOpen = false;
    public FilteredCustomers: CustomerDisplayObject[] | null = null;

    public PreviewedFileId$ = new BehaviorSubject<number | null>(null);

    public ContractForm = this.fb.group({
        Abrechnungszyklus: null,
        ContractGroupType: [null, arrayLengthValidator(0, 1)],
        ContractSign: null,
        ContractStatusId: null,
        Description: null,
        EndDate: null,
        Name: [null, Validators.required],
        Number: [null, Validators.required],
        StartDate: null,
        VertragsNummer: null,
        SachkontoDebitor: null,
        SachkontoKreditor: null,
        HatDiagnostik: false,
    }) as FormGroupTyped<{
        Abrechnungszyklus: string | null;
        ContractGroupType: ContractGroupTypeEntity[];
        ContractSign: string | null;
        ContractStatusId: number;
        Description: string | null;
        EndDate: Date | null;
        Name: string | null;
        Number: string | null;
        StartDate: Date | null;
        VertragsNummer: string | null;
        SachkontoDebitor: string | null;
        SachkontoKreditor: string | null;
        HatDiagnostik: boolean;
    }>;
    public ContractOptionsForm = this.fb.group({
        Casenumber: null,
        DtaOptions: null,
        TeGueltigkeitDauerUnit: null,
        TeGueltigkeitDauerValue: null,
        EinreichfristNachQuartalEndeInWochen: [null, numberValidator()],
        HatAnamnese: null,
        IstWkv: null,
        KuendigungsfristInTagen: [null, numberValidator()],
        LaufzeitInMonaten: [null, numberValidator()],
        MedizinprodukteEigenleistungErlaubt: null,
        MindestalterVersicherter: [null, numberValidator()],
        TeAnzahlSeiten: [null, numberValidator()],
        TeGueltigkeitId: null,
        WiderrufsfristInTagen: [null, numberValidator()],
    }) as FormGroupTyped<{
        Casenumber: string | null;
        DtaOptions: string | null;
        TeGueltigkeitDauerUnit: string | null;
        TeGueltigkeitDauerValue: number | null;
        EinreichfristNachQuartalEndeInWochen: number | null;
        HatAnamnese: boolean;
        IstWkv: boolean;
        KuendigungsfristInTagen: number | null;
        LaufzeitInMonaten: number | null;
        MedizinprodukteEigenleistungErlaubt: boolean | null;
        MindestalterVersicherter: number;
        TeAnzahlSeiten: number | null;
        TeGueltigkeitId: number | null;
        WiderrufsfristInTagen: number | null;
    }>;
    constructor(
        private store: Store<State>,
        private route: ActivatedRoute,
        private dialog: MatDialog,
        private routingHistoryService: RoutingHistoryService,
        private fb: FormBuilder,
        private datePipe: DatePipe,
        @Inject(LOCALE_ID) private locale: string,
        folderDataService: FolderDataService,
        private PS: PermissionService,
    ) {
        const contract$ = combineLatest([
            route.paramMap.pipe(
                map((paramMap) => +paramMap.get('contractId')),
                distinctUntilChanged(),
            ),
            this.PS.Has$(this.PS.Permissions.GetCustomer),
        ]).pipe(
            filter(([_, getCustomer]) => getCustomer),
            map(([contractId]) => contractId),
            switchMap((contractId) =>
                store.select(getContractById({ id: contractId })).pipe(
                    tap((contract) => !contract && console.error('Could not find Contract')),
                    filter(isNotNullOrUndefined),
                ),
            ),
            shareReplay(1),
        );
        const contractOptions$ = contract$.pipe(
            switchMap((contract) =>
                this.store.select(getContractOptionsByContractId, contract.Id).pipe(
                    tap((contractOption) => !contractOption && console.error('Could not find ContractOption')),
                    filter(isNotNullOrUndefined),
                ),
            ),
        );
        this.FolderId$ = contract$.pipe(
            switchMap((contract) => folderDataService.getFolderFromEntity(contract.Id, FolderTypes.contract)),
            filter(isNotNullOrUndefined),
            map((v) => v.Id),
            distinctUntilChanged(),
        );

        this.ProfileData$ = combineLatest([
            contract$,
            this.store.select(getContractStatus).pipe(filter(isNotNullOrUndefined)),
            this.store.select(getTEGueltigkeiten).pipe(filter(isNotNullOrUndefined)),
            contractOptions$,
            this.contractGroupTypeList$,
        ]).pipe(
            map(([contract, contractStatus, teGueltigkeiten, contractOptions, contractGroupTypes]): IProfileTemplateData => {
                return {
                    Icon: 'file-signature',
                    Properties: [
                        {
                            formControl: this.ContractForm.controls.Name,
                            key: 'Name',
                            value: contract.Name,
                        },
                        {
                            formControl: this.ContractForm.controls.Description,
                            key: 'Beschreibung',
                            value: contract.Description,
                        },
                        {
                            formControl: this.ContractForm.controls.Number,
                            key: 'Nummer',
                            value: contract.Number,
                        },
                        {
                            formControl: this.ContractForm.controls.VertragsNummer,
                            key: 'Vertragsnummer',
                            value: contract.VertragsNummer,
                        },
                        {
                            formControl: this.ContractForm.controls.ContractSign,
                            key: 'Vertragskennzeichen',
                            value: contract.ContractSign,
                        },
                        {
                            formControl: this.ContractForm.controls.StartDate,
                            key: 'Startdatum',
                            value: contract.StartDate ? datePipe.transform(contract.StartDate) : null,
                            options: { type: CustomPropertyType.Datum, specialInput: {} },
                        },
                        {
                            formControl: this.ContractForm.controls.EndDate,
                            key: 'Ablaufdatum',
                            value: contract.EndDate ? datePipe.transform(contract.EndDate) : null,
                            options: { type: CustomPropertyType.Datum, specialInput: {} },
                        },
                        {
                            formControl: this.ContractForm.controls.ContractStatusId,
                            key: 'Vertragsstatus',
                            value: contract.ContractStatusId ? contractStatus.find((c) => c.Id === contract.ContractStatusId)?.Name : null,
                            options: {
                                specialInput: {
                                    select: contractStatus.map((c) => ({
                                        optionValue: c.Id,
                                        optionLabel: c.Name,
                                    })),
                                },
                            },
                        },
                        {
                            formControl: this.ContractForm.controls.Abrechnungszyklus,
                            key: 'Abrechnungszyklus',
                            value: contract.Abrechnungszyklus,
                        },
                        {
                            formControl: this.ContractOptionsForm.controls.DtaOptions,
                            key: 'DTA Optionen',
                            value: contractOptions.DtaOptions,
                        },
                        {
                            formControl: this.ContractOptionsForm.controls.Casenumber,
                            key: 'Fallnummer',
                            value: contractOptions.Casenumber,
                        },
                        {
                            formControl: this.ContractOptionsForm.controls.HatAnamnese,
                            key: 'Hat Anamnese',
                            value: contractOptions.HatAnamnese ? 'Ja' : 'Nein',
                            options: {
                                type: CustomPropertyType.Boolean,
                                specialInput: {},
                            },
                        },
                        {
                            formControl: this.ContractOptionsForm.controls.IstWkv,
                            key: 'Ist WKV',
                            value: contractOptions.IstWkv ? 'Ja' : 'Nein',
                            options: {
                                type: CustomPropertyType.Boolean,
                                specialInput: {},
                            },
                        },
                        {
                            formControl: this.ContractOptionsForm.controls.WiderrufsfristInTagen,
                            key: 'Widerrufsfrist in Tagen',
                            value: contractOptions.WiderrufsfristInTagen + '',
                            options: { specialInput: { number: true } },
                        },
                        {
                            formControl: this.ContractOptionsForm.controls.KuendigungsfristInTagen,
                            key: 'Kündigungsfrist in Tagen',
                            value: contractOptions.KuendigungsfristInTagen + '',
                            options: { specialInput: { number: true } },
                        },
                        {
                            formControl: this.ContractOptionsForm.controls.LaufzeitInMonaten,
                            key: 'Laufzeit in Monaten',
                            value: contractOptions.LaufzeitInMonaten + '',
                            options: { specialInput: { number: true } },
                        },
                        {
                            formControl: this.ContractOptionsForm.controls.EinreichfristNachQuartalEndeInWochen,
                            key: 'Einreichfrist nach Quartalende in Wochen',
                            value: contractOptions.EinreichfristNachQuartalEndeInWochen + '',
                            options: { specialInput: { number: true } },
                        },
                        {
                            formControl: this.ContractOptionsForm.controls.MindestalterVersicherter,
                            key: 'Mindestalter Versicherter',
                            value: contractOptions.MindestalterVersicherter + '',
                            options: { specialInput: { number: true } },
                        },
                        {
                            formControl: this.ContractOptionsForm.controls.TeGueltigkeitId,
                            key: 'TE Gueltigkeit',
                            value: contractOptions.TeGueltigkeitId ? teGueltigkeiten.find((t) => t.Id === contractOptions.TeGueltigkeitId)?.Name : null,
                            options: {
                                specialInput: {
                                    select: teGueltigkeiten.map((c) => ({
                                        optionValue: c.Id,
                                        optionLabel: c.Name,
                                    })),
                                },
                            },
                        },
                        {
                            key: 'TE Gültigkeit Dauer',
                            value:
                                contractOptions.TEGueltigkeitDauerValue != null && contractOptions.TEGueltigkeitDauerUnit != null
                                    ? contractOptions.TEGueltigkeitDauerValue +
                                      ' ' +
                                      (contractOptions.TEGueltigkeitDauerValue == 1
                                          ? TEGueltigkeitDauerUnits.find((t) => t.value === contractOptions.TEGueltigkeitDauerUnit)?.single
                                          : TEGueltigkeitDauerUnits.find((t) => t.value === contractOptions.TEGueltigkeitDauerUnit)?.multiple)
                                    : null,
                        },
                        {
                            formControl: this.ContractOptionsForm.controls.TeGueltigkeitDauerValue,
                            key: 'TE Gültigkeit Dauer',
                            options: { specialInput: { number: true } },
                        },
                        {
                            formControl: this.ContractOptionsForm.controls.TeGueltigkeitDauerUnit,
                            key: 'TE Gueltigkeit Einheit',
                            options: {
                                specialInput: {
                                    select: TEGueltigkeitDauerUnits.map((c) => ({
                                        optionValue: c.value,
                                        optionLabel: c.multiple,
                                    })),
                                },
                            },
                        },
                        {
                            formControl: this.ContractOptionsForm.controls.TeAnzahlSeiten,
                            key: 'TE Anzahl Seiten',
                            value: contractOptions.TeAnzahlSeiten + '',
                            options: { specialInput: { number: true } },
                        },
                        {
                            formControl: this.ContractOptionsForm.controls.MedizinprodukteEigenleistungErlaubt,
                            key: 'Medizinprodukte Eigenleistung erlaubt',
                            value: contractOptions.MedizinprodukteEigenleistungErlaubt ? 'Ja' : 'Nein',
                            options: {
                                type: CustomPropertyType.Boolean,
                                specialInput: {},
                            },
                        },
                        {
                            formControl: this.ContractForm.controls.ContractGroupType,
                            key: 'Vertragsgruppe',
                            value: contract.ContractGroupTypeId ? contractGroupTypes.find((c) => c.Id === contract.ContractGroupTypeId)?.Name : null,
                            options: {
                                specialInput: {
                                    chipAutocomplete: {
                                        MapFn: (option) => option?.Name,
                                        Options: contractGroupTypes,
                                    },
                                },
                            },
                        },
                        {
                            formControl: this.ContractForm.controls.SachkontoDebitor,
                            key: 'Sachkonto Debitor',
                            value: contract.SachkontoDebitor,
                        },
                        {
                            formControl: this.ContractForm.controls.SachkontoKreditor,
                            key: 'Sachkonto Kreditor',
                            value: contract.SachkontoKreditor,
                        },
                        {
                            formControl: this.ContractForm.controls.HatDiagnostik,
                            key: 'Hat Diagnostik',
                            value: contract.HatDiagnostik ? 'Ja' : 'Nein',
                            options: {
                                type: CustomPropertyType.Boolean,
                                specialInput: {},
                            },
                        },
                    ],
                };
            }),
        );
        this.SearchedCustomerList$ = combineLatest([
            contract$,
            this.store.select(getCustomers).pipe(filter(isNotNullOrUndefined)),
            this.store.select(getContractParticipants).pipe(filter(isNotNullOrUndefined)),
            this.CustomerSearchForm.valueChanges.pipe(startWith('')),
        ]).pipe(
            map(([contract, customers, contractParticipants, searchString]) => {
                const cps = contractParticipants.filter((cp) => cp.ContractId === contract.Id);

                const searchTerm = searchString?.toLowerCase() || null;
                return customers
                    .filter((customer) => !!cps.find((cp) => cp.CustomerId === customer.Id) && (!searchTerm || (customer.Name && customer.Name.toLowerCase().includes(searchTerm)) || (customer.CustomerNo && customer.CustomerNo.toLowerCase().includes(searchTerm))))
                    .sort((a, b) => {
                        if (a.CustomerNo && !b.CustomerNo) {
                            return 1;
                        }
                        return -1;
                    })
                    .map<CustomerDisplayObject>(customer => {
                        const aps = cps.filter(cp => cp.CustomerId === customer.Id);
                            const date = aps[0]?.EntryDate;
                            const endDate = aps[0]?.EndDate;
                            const ausnahmeDatum = aps[0]?.Ausnahmedatum;
                        return {
                            Id: customer.Id,
                            CustomerTypeIds: customer.CustomerTypeIds,
                            CustomerNo: customer.CustomerNo,
                            Name: customer.Name,
                            Duration: date && endDate ? formatDate(date, 'shortDate', 'de-DE')+" - "+formatDate(endDate, 'shortDate', 'de-DE') : date ? "ab "+ formatDate(date, 'shortDate', 'de-DE'): endDate ? "bis " + formatDate(endDate, 'shortDate', 'de-DE'):  '',
                            AusnahmeDatum: ausnahmeDatum ? formatDate(ausnahmeDatum, 'shortDate', 'de-DE') : '',
                        }
                    });
            }),
        );
        this.subscriptions.push(
            this.Save$.pipe(
                withLatestFrom(contract$, contractOptions$),
                tap(([, contract, contractOptions]) => {
                    if (this.ContractForm.invalid || this.ContractOptionsForm.invalid) {
                        return;
                    }
                    const value = this.ContractForm.value;
                    this.store.dispatch(
                        new PatchContractRequest({
                            id: contract.Id,
                            name: value.Name,
                            number: value.Number,
                            contractSign: value.ContractSign,
                            contractGroupTypeId: value.ContractGroupType && value.ContractGroupType[0] && value.ContractGroupType[0].Id,
                            description: value.Description,
                            startDate: FrontendDate(value.StartDate),
                            endDate: FrontendDate(value.EndDate),
                            contractStatusId: value.ContractStatusId,
                            abrechnungszyklus: value.Abrechnungszyklus,
                            vertragsNummer: value.VertragsNummer,
                            sachkontoDebitor: value.SachkontoDebitor,
                            sachkontoKreditor: value.SachkontoKreditor,
                            hatDiagnostik: value.HatDiagnostik,
                        }),
                    );
                    const value2 = this.ContractOptionsForm.value;
                    this.store.dispatch(
                        new PatchContractOptionsRequest({
                            id: contractOptions.Id,
                            dtaOptions: value2.DtaOptions,
                            teGueltigkeitDauerValue: isNotNullOrUndefined(value2.TeGueltigkeitDauerValue) ? +value2.TeGueltigkeitDauerValue : null,
                            teGueltigkeitDauerUnit: value2.TeGueltigkeitDauerUnit,
                            casenumber: value2.Casenumber,
                            hatAnamnese: value2.HatAnamnese,
                            istWkv: value2.IstWkv,
                            widerrufsfristInTagen: isNotNullOrUndefined(value2.WiderrufsfristInTagen) ? +value2.WiderrufsfristInTagen : null,
                            kuendigungsfristInTagen: isNotNullOrUndefined(value2.KuendigungsfristInTagen) ? +value2.KuendigungsfristInTagen : null,
                            laufzeitInMonaten: isNotNullOrUndefined(value2.LaufzeitInMonaten) ? +value2.LaufzeitInMonaten : null,
                            einreichfristNachQuartalEndeInWochen: isNotNullOrUndefined(value2.EinreichfristNachQuartalEndeInWochen) ? +value2.EinreichfristNachQuartalEndeInWochen : null,
                            mindestalterVersicherter: isNotNullOrUndefined(value2.MindestalterVersicherter) ? +value2.MindestalterVersicherter : null,
                            teGueltigkeitId: isNotNullOrUndefined(value2.TeGueltigkeitId) ? +value2.TeGueltigkeitId : null,
                            teAnzahlSeiten: isNotNullOrUndefined(value2.TeAnzahlSeiten) ? +value2.TeAnzahlSeiten : null,
                            medizinprodukteEigenleistungErlaubt: value2.MedizinprodukteEigenleistungErlaubt,
                        }),
                    );
                    this.CloseEdit();
                }),
            ).subscribe(),
            this.OpenEdit$.pipe(
                withLatestFrom(contract$, contractOptions$, this.contractGroupTypeList$),
                tap(([, contract, contractOptions, contractGroupTypes]) => {
                    this.ContractForm.setValue({
                        Abrechnungszyklus: contract.Abrechnungszyklus,
                        ContractGroupType: contract.ContractGroupTypeId ? [contractGroupTypes.find((cgt) => cgt.Id === contract.ContractGroupTypeId)] : null,
                        ContractSign: contract.ContractSign,
                        ContractStatusId: contract.ContractStatusId,
                        Description: contract.Description,
                        EndDate: contract.EndDate,
                        Name: contract.Name,
                        Number: contract.Number,
                        StartDate: contract.StartDate,
                        VertragsNummer: contract.VertragsNummer,
                        SachkontoDebitor: contract.SachkontoDebitor,
                        SachkontoKreditor: contract.SachkontoKreditor,
                        HatDiagnostik: contract.HatDiagnostik,
                    });
                    this.ContractOptionsForm.setValue({
                        Casenumber: contractOptions.Casenumber,
                        DtaOptions: contractOptions.DtaOptions,
                        TeGueltigkeitDauerUnit: contractOptions.TEGueltigkeitDauerUnit,
                        TeGueltigkeitDauerValue: contractOptions.TEGueltigkeitDauerValue,
                        EinreichfristNachQuartalEndeInWochen: contractOptions.EinreichfristNachQuartalEndeInWochen,
                        HatAnamnese: contractOptions.HatAnamnese,
                        IstWkv: contractOptions.IstWkv,
                        KuendigungsfristInTagen: contractOptions.KuendigungsfristInTagen,
                        LaufzeitInMonaten: contractOptions.LaufzeitInMonaten,
                        MedizinprodukteEigenleistungErlaubt: contractOptions.MedizinprodukteEigenleistungErlaubt,
                        MindestalterVersicherter: contractOptions.MindestalterVersicherter,
                        TeAnzahlSeiten: contractOptions.TeAnzahlSeiten,
                        TeGueltigkeitId: contractOptions.TeGueltigkeitId,
                        WiderrufsfristInTagen: contractOptions.WiderrufsfristInTagen,
                    });
                    this.Editing$.next(true);
                }),
            ).subscribe(),
            this.OpenDeletedDialog$.pipe(
                withLatestFrom(contract$),
                tap(([, contract]) => {
                    const dialogRef = this.dialog.open(DeletedDialogComponent, {
                        width: '90%',
                        maxWidth: '400px',
                        data: {
                            name: `Vertrag ${contract.Name} wirklich löschen?`,
                        },
                    });
                    dialogRef
                        .afterClosed()
                        .pipe(take(1))
                        .subscribe((result) => {
                            if (result) {
                                this.store.dispatch(new DeleteContractRequest({ id: contract.Id }));
                                this.routingHistoryService.NavigateBackwards();
                            }
                        });
                }),
            ).subscribe(),
            this.OpenCustomerSelect$.pipe(
                withLatestFrom(contract$),
                tap(([, contract]) => {
                    this.dialog.open<SelectCustomersForContractDialogComponent, SelectCustomersForContractDialogData>(SelectCustomersForContractDialogComponent, {
                        ...SelectCustomersForContractDialogComponent.DefaultConfig,
                        data: {
                            contractId: contract.Id,
                        },
                    });
                }),
            ).subscribe(),
        );
    }

    public CloseEdit() {
        this.Editing$.next(false);
    }

    ngOnDestroy() {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    protected readonly CustomerAdministrationMeta = CustomerAdministrationMeta;
}
